import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Flex,
  HStack,
  SimpleGrid,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { FaEdit, FaPlus } from 'react-icons/fa';
import { PiTrash } from 'react-icons/pi';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import BackButton from 'components/backButton/BackButton';
import CommonPagination from 'components/commonPagination/CommonPagination';
import CommonTable from 'components/commonTable/CommonTable';
import DeleteModal from 'components/popUpModel/DeleteModal';
import PageLoader from 'components/wholePageLoader/PageLoader';
import { useColorModeVariables } from 'components/colorModeVarialbels/ColorModeValues';
import LabelBadge from 'components/labels/LabelBadge';
import useLabelDetail from 'hooks/useLabelDetail';
import {
  canManageLabels,
  GLOBAL_LABEL_SCOPE_REGION_ID,
  sideBarRoutes,
} from 'utils/StaticData';
import {
  showConfirmationAlert,
  showErrorAlert,
  showSuccessAlert,
} from 'utils/AlertUtils';
import { assignLabelMerchants } from 'utils/labelApi';
import LabelMerchantAssignModal from './LabelMerchantAssignModal';

const columnHelper = createColumnHelper();

// Detail page (RAT-1688/1699): the label's own info plus every merchant
// currently assigned to it. Merchant assignment itself happens on the
// merchant's Edit page (RAT-1689) - this view only displays it. Edit/Delete
// for the label itself live here (moved off the list page) and are gated the
// same way as everywhere else: Super Admin manages any label, a Dealer only its
// own region's, and a Seller none.
const LabelDetail = () => {
  const { labelId } = useParams();
  const navigate = useNavigate();
  const { tabletextColor } = useColorModeVariables();
  const { adminDetails } = useSelector((state) => state.authSlice);
  const isSuperAdmin = adminDetails?.role === sideBarRoutes?.Admin;
  const ownRegionId = adminDetails?.region?.regionId;
  const {
    label,
    merchants,
    total,
    limit,
    isLabelLoading,
    isMerchantsLoading,
    isDeleting,
    fetchLabel,
    fetchMerchants,
    deleteLabel,
  } = useLabelDetail();

  const [page, setPage] = useState(1);
  const [removingMerchantId, setRemovingMerchantId] = useState(null);
  const deleteModal = useDisclosure();
  const assignModal = useDisclosure();

  useEffect(() => {
    if (labelId) fetchLabel(labelId);
  }, [labelId, fetchLabel]);

  useEffect(() => {
    if (labelId) fetchMerchants(labelId, { page });
  }, [labelId, page, fetchMerchants]);

  // Two separate questions. First the role: a Seller reaches this page to view
  // and assign, but maintains no label at all, not even one in its own region -
  // without that check it would qualify below, since it carries a region like
  // every other non-Super-Admin account. Then the scope: a Global or another
  // region's label is Super-Admin-only.
  const canManage =
    isSuperAdmin ||
    (canManageLabels(adminDetails?.role) &&
      label?.regionId !== GLOBAL_LABEL_SCOPE_REGION_ID &&
      label?.regionId === ownRegionId);

  const handleDelete = async () => {
    const ok = await deleteLabel(labelId);
    if (!ok) return;
    deleteModal.onClose();
    navigate('/admin/labels');
  };

  const refreshAssignments = () => {
    fetchLabel(labelId);
    fetchMerchants(labelId, { page });
  };

  const handleUnassign = async (merchant) => {
    const { isConfirmed } = await showConfirmationAlert(
      `Remove "${label?.name}" from ${merchant.name}?`,
    );
    if (!isConfirmed) return;

    setRemovingMerchantId(merchant.merchantId);
    try {
      await assignLabelMerchants(labelId, { remove: [merchant.merchantId] });
      showSuccessAlert('Label removed from merchant');
      refreshAssignments();
    } catch (error) {
      showErrorAlert(
        error?.message || error?.error?.message || 'Failed to remove the label',
      );
    } finally {
      setRemovingMerchantId(null);
    }
  };

  const cellText = (children) => (
    <Text
      color={tabletextColor}
      fontSize="sm"
      fontWeight="600"
      align="left"
      whiteSpace="nowrap"
    >
      {children}
    </Text>
  );

  const headerText = (children) => (
    <Text
      whiteSpace="nowrap"
      align="left"
      fontSize={{ sm: '10px', lg: '12px' }}
      color="gray.400"
      fontFamily="Montserrat"
    >
      {children}
    </Text>
  );

  const columns = [
    columnHelper.accessor((row) => row?.merchantId, {
      id: 'merchantId',
      header: () => headerText('Merchant ID'),
      cell: (info) => cellText(info.getValue()),
    }),
    columnHelper.accessor((row) => row?.name, {
      id: 'name',
      header: () => headerText('Merchant Name'),
      cell: (info) => cellText(info.getValue()),
    }),
    columnHelper.accessor((row) => row?.regionName, {
      id: 'regionName',
      header: () => headerText('Region'),
      cell: (info) => cellText(info.getValue()),
    }),
    // Assigning is not editing the label: any role that reaches this page may put
    // a label on the merchants it can see, even one it cannot rename (RAT-1689).
    // The table only ever lists merchants in the caller's own scope, so there is
    // no row here that the caller may not unassign.
    columnHelper.display({
      id: 'unassign',
      header: () => headerText('Action'),
      cell: ({ row }) => (
        <Button
          size="xs"
          variant="outline"
          colorScheme="red"
          isLoading={removingMerchantId === row.original.merchantId}
          // The row itself links to the merchant, so the click must not
          // bubble into that navigation.
          onClick={(event) => {
            event.stopPropagation();
            handleUnassign(row.original);
          }}
        >
          Remove
        </Button>
      ),
    }),
  ];

  if (isLabelLoading) {
    return <PageLoader />;
  }

  return (
    <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
      <Flex justifyContent="space-between" mb={4} gap="3">
        <BackButton fallbackUrl="/admin/labels" label="Back to Labels" />
        {canManage && (
          <HStack spacing="3">
            <Button
              py="10px"
              px="15px"
              fontWeight="semibold"
              borderRadius="8px"
              bg="primary"
              color="white"
              _hover={{ bg: 'primary', opacity: 0.8 }}
              type="button"
              leftIcon={<FaEdit size={22} />}
              onClick={() => navigate(`/admin/labels/edit/${labelId}`)}
            >
              Edit Label
            </Button>
            <Button
              py="10px"
              px="15px"
              fontWeight="semibold"
              borderRadius="8px"
              bg="red.500"
              color="white"
              _hover={{ bg: 'red.500', opacity: 0.8 }}
              type="button"
              leftIcon={<PiTrash size={22} />}
              onClick={deleteModal.onOpen}
            >
              Delete Label
            </Button>
          </HStack>
        )}
      </Flex>

      <Card
        w="100%"
        boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
        borderRadius="20px"
        paddingInline="40px"
        paddingBlock="40px"
        mb={6}
      >
        <Text fontSize="2xl" fontWeight="bold" mb={6}>
          Label Details
        </Text>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Flex
            alignItems={{ sm: 'start', md: 'center' }}
            direction={{ sm: 'column', md: 'row' }}
            gap="10px"
            padding="4px 8px"
            borderLeft={'1px solid #ccc'}
          >
            <Text fontWeight="normal" width="80px">
              ID :
            </Text>
            <Text fontWeight="semibold" fontSize="lg">
              {label?.id || 'N/A'}
            </Text>
          </Flex>

          <Flex
            alignItems={{ sm: 'start', md: 'center' }}
            direction={{ sm: 'column', md: 'row' }}
            gap="10px"
            padding="4px 8px"
            borderLeft={'1px solid #ccc'}
          >
            <Text fontWeight="normal" width="80px">
              Label :
            </Text>
            <LabelBadge label={label} />
          </Flex>

          <Flex
            alignItems={{ sm: 'start', md: 'center' }}
            direction={{ sm: 'column', md: 'row' }}
            gap="10px"
            padding="4px 8px"
            borderLeft={'1px solid #ccc'}
          >
            <Text fontWeight="normal" width="80px">
              Scope :
            </Text>
            <Text fontWeight="semibold">{label?.regionName || 'N/A'}</Text>
          </Flex>
        </SimpleGrid>
      </Card>

      <Card
        w="100%"
        boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
        borderRadius="20px"
        paddingInline="40px"
        paddingBlock="30px"
        mb={6}
      >
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontSize="xl" fontWeight="bold">
            Assigned Merchants
          </Text>
          <Flex align="center" gap="4">
            {/* The count is scoped to what this role may see, so a Dealer and a
                Super Admin can legitimately read different totals for the same
                label (RAT-1689). */}
            <Text fontSize="lg" fontWeight="bold">
              {label?.merchantCount ?? 0}
            </Text>
            <Button
              size="sm"
              fontWeight="semibold"
              borderRadius="8px"
              bg="primary"
              color="white"
              _hover={{ bg: 'primary', opacity: 0.8 }}
              leftIcon={<FaPlus size={14} />}
              onClick={assignModal.onOpen}
            >
              Assign Merchants
            </Button>
          </Flex>
        </Flex>

        <CommonTable
          columns={columns}
          data={merchants}
          isLoading={isMerchantsLoading}
          isLink
          parentLink="/admin/merchants/details"
          keyValue="merchantId"
          rowclick={[]}
          emptyStateMessage="No merchants assigned to this label yet."
        />

        {total > limit && (
          <CommonPagination
            totalCount={total}
            activePage={page}
            pageCount={Math.ceil(total / (limit || 10))}
            onPageChange={setPage}
            itemsPerPage={limit || 10}
          />
        )}
      </Card>

      <DeleteModal
        title={`Delete "${label?.name}"? This can't be undone.`}
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.onClose}
        SolidBTNText="Delete"
        loader={isDeleting}
        onDelete={handleDelete}
      />

      <LabelMerchantAssignModal
        isOpen={assignModal.isOpen}
        onClose={assignModal.onClose}
        labelId={labelId}
        labelName={label?.name}
        onSaved={refreshAssignments}
      />
    </Box>
  );
};

export default LabelDetail;
